import React, {PropsWithChildren} from 'react';
import {getBaseHash, Router} from '../router/Router';
import {Provider} from 'react-redux';
import {Store} from 'redux';
import {ErrorHandler, initializeErrorHandler} from '../errorhandler/GlobalErrorHandler';
import {useHistory} from 'react-router-dom';
import {initializeGlobalHistory} from '../history/History';

/**
 * {@docCategory ApplicationContext}
 */
export interface ApplicationContextProps {
   /**
    * Set the redux store. This property takes priority over the storeProvider function.
    * If you don't specify a store or storeProvider, then the redux provider will not be configured.
    */
   store?: Store;

   /**
    * Returns the redux store, when using redux. If you use the route, note that the route is always
    * the base part of the route. For example, if the URL looks like "https://test.unifocus.com/test/index.html/#/main/123"
    * then the route passed to the function will be "/main".
    *
    * If you don't specify a storeProvider, then the redux provider will not be configured.
    *
    * @param route - the route string
    */
   storeProvider?: (route: string) => Store;

   /**
    * The error handler.
    */
   errorHandler: ErrorHandler;
}

export const ApplicationContext = (props: PropsWithChildren<ApplicationContextProps>) => {
   initializeErrorHandler(props.errorHandler);

   return (
      <Router>
         <GlobalHistoryInitializer>
            {wrapInReduxProvider(props)}
         </GlobalHistoryInitializer>
      </Router>
   );
};

interface HistoryWrapperProps {
}

const GlobalHistoryInitializer = (props: PropsWithChildren<HistoryWrapperProps>) => {
   const history = useHistory();
   initializeGlobalHistory(history);

   return (
      <>
         {props.children}
      </>
   );
};

const wrapInReduxProvider = (props: PropsWithChildren<ApplicationContextProps>) => {
   const store = props.store || (props.storeProvider ? props.storeProvider(getBaseHash()) : undefined);

   if (store) {
      return (
         <Provider store={store}>
            {props.children}
         </Provider>
      );
   }

   return props.children;
};
